/**
 * Fill missing job-journey IDs and human labels from related-record lookups.
 * Discovers provisional/draft quotes by leadId when conversion ids are absent.
 */

import { getQuoteById, getQuotes } from "../../features/quotes/services/quoteService.js";
import { getInvoices } from "../../features/invoices/services/invoiceService.js";
import { getLead } from "../../features/leads/services/leadService.js";
import { getCustomer } from "../../features/customers/services/customerService.js";
import {
  customerDisplayFrom,
  invoiceDisplayFrom,
  leadDisplayFrom,
  normalizeInvoiceList,
  normalizeQuoteList,
  pickDisplayLabel,
  pickEntityId,
  pickPrimaryInvoice,
  pickPrimaryQuote,
  quoteDisplayFrom,
} from "./buildJobJourneySteps.js";

/**
 * @param {{
 *   current: string,
 *   customerId?: string|null,
 *   leadId?: string|null,
 *   quoteId?: string|null,
 *   invoiceId?: string|null,
 *   orderId?: string|null,
 *   customerDisplay?: string|null,
 *   leadDisplay?: string|null,
 *   quoteDisplay?: string|null,
 *   invoiceDisplay?: string|null,
 *   orderDisplay?: string|null,
 * }} seed
 * @param {{
 *   getQuoteById?: typeof getQuoteById,
 *   getQuotes?: typeof getQuotes,
 *   getInvoices?: typeof getInvoices,
 *   getLead?: typeof getLead,
 *   getCustomer?: typeof getCustomer,
 * }} [deps]
 */
export async function resolveJobJourneyLinks(seed, deps = {}) {
  const fetchQuote = deps.getQuoteById || getQuoteById;
  const fetchQuotes = deps.getQuotes || getQuotes;
  const fetchInvoices = deps.getInvoices || getInvoices;
  const fetchLead = deps.getLead || getLead;
  const fetchCustomer = deps.getCustomer || getCustomer;

  const next = {
    current: seed.current,
    customerId: pickEntityId(seed.customerId),
    leadId: pickEntityId(seed.leadId),
    quoteId: pickEntityId(seed.quoteId),
    invoiceId: pickEntityId(seed.invoiceId),
    orderId: pickEntityId(seed.orderId),
    customerDisplay: pickDisplayLabel(seed.customerDisplay),
    leadDisplay: pickDisplayLabel(seed.leadDisplay),
    quoteDisplay: pickDisplayLabel(seed.quoteDisplay),
    invoiceDisplay: pickDisplayLabel(seed.invoiceDisplay),
    orderDisplay: pickDisplayLabel(seed.orderDisplay),
  };

  // Discover provisional/draft quotes linked by leadId (no conversion id yet).
  if (next.leadId && !next.quoteId) {
    try {
      const listed = await fetchQuotes({ leadId: next.leadId, limit: 10 });
      if (listed && listed.success !== false) {
        const rows = normalizeQuoteList(listed);
        const primary = pickPrimaryQuote(rows);
        if (primary) {
          next.quoteId = pickEntityId(primary.id, primary._id);
          if (!next.quoteDisplay) next.quoteDisplay = quoteDisplayFrom(primary);
          if (!next.customerId) {
            next.customerId = pickEntityId(primary.customerId);
          }
          if (!next.customerDisplay) {
            next.customerDisplay = pickDisplayLabel(primary.customerName);
          }
          if (!next.orderId) {
            next.orderId = pickEntityId(
              primary.orderId,
              primary.payment?.orderId
            );
          }
          if (!next.orderDisplay) {
            next.orderDisplay = pickDisplayLabel(
              primary.orderId,
              primary.payment?.orderId
            );
          }
        }
      }
    } catch {
      // keep partial links
    }
  }

  const needsQuote =
    Boolean(next.quoteId) &&
    (!next.leadId ||
      !next.orderId ||
      !next.customerId ||
      !next.customerDisplay ||
      !next.quoteDisplay ||
      !next.orderDisplay);

  const needsInvoice =
    Boolean(next.quoteId) &&
    (!next.invoiceId ||
      !next.invoiceDisplay ||
      !next.customerDisplay ||
      !next.orderId);

  const [quoteRes, invoiceRes] = await Promise.all([
    needsQuote
      ? fetchQuote(next.quoteId).catch(() => null)
      : Promise.resolve(null),
    needsInvoice
      ? fetchInvoices({ quoteId: next.quoteId }).catch(() => null)
      : Promise.resolve(null),
  ]);

  if (quoteRes?.success && quoteRes.data) {
    const quote = quoteRes.data;
    if (!next.leadId) next.leadId = pickEntityId(quote.leadId);
    if (!next.orderId) {
      next.orderId = pickEntityId(quote.orderId, quote.payment?.orderId);
    }
    if (!next.customerId) next.customerId = pickEntityId(quote.customerId);
    if (!next.customerDisplay) {
      next.customerDisplay = pickDisplayLabel(quote.customerName);
    }
    if (!next.quoteDisplay) next.quoteDisplay = quoteDisplayFrom(quote);
    if (!next.orderDisplay) {
      next.orderDisplay = pickDisplayLabel(
        quote.orderId,
        quote.payment?.orderId
      );
    }
  }

  if (invoiceRes?.success) {
    const invoices = normalizeInvoiceList(invoiceRes.data);
    const match =
      pickPrimaryInvoice(invoices, next.quoteId) ||
      invoices.find(
        (inv) => pickEntityId(inv.id, inv._id) === next.invoiceId
      ) ||
      null;

    if (match) {
      if (!next.invoiceId) {
        next.invoiceId = pickEntityId(match.id, match._id);
      }
      if (!next.invoiceDisplay) {
        next.invoiceDisplay = invoiceDisplayFrom(match);
      }
      if (!next.orderId) {
        next.orderId = pickEntityId(match.jobId, match.orderId);
      }
      if (!next.orderDisplay) {
        next.orderDisplay = pickDisplayLabel(match.jobId, match.orderId);
      }
      if (!next.leadId) next.leadId = pickEntityId(match.leadId);
      if (!next.customerId) next.customerId = pickEntityId(match.customerId);
      if (!next.customerDisplay) {
        next.customerDisplay = pickDisplayLabel(match.customerName);
      }
    }
  }

  if (next.leadId && !next.leadDisplay) {
    try {
      const leadRes = await fetchLead(next.leadId);
      if (leadRes?.success && leadRes.data) {
        const lead = leadRes.data;
        next.leadDisplay = leadDisplayFrom(lead);
        if (!next.customerId) next.customerId = pickEntityId(lead.customerId);
        if (!next.customerDisplay) {
          next.customerDisplay = pickDisplayLabel(lead.customerName, lead.name);
        }
        if (!next.quoteId) {
          next.quoteId = pickEntityId(
            lead.convertedQuoteId,
            lead.tracking?.conversionData?.quoteId,
            lead.automatedPricing?.quoteId
          );
        }
      }
    } catch {
      // keep partial links
    }
  }

  if (next.customerId && !next.customerDisplay) {
    try {
      const customerRes = await fetchCustomer(next.customerId);
      if (customerRes?.success && customerRes.data) {
        next.customerDisplay = customerDisplayFrom(customerRes.data);
      }
    } catch {
      // keep partial links
    }
  }

  return next;
}
